import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Item } from '@/components/ProjectHomePage/BundlesSection/BundleCard';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { localUserID, user } from '@/atoms/user';
import { addToCart } from '@/services/Cart';
import { IBookShop } from '@/utils/interfaces/BooksShop';
import { AddToCartProps } from '@/utils/interfaces/cart';
import { User } from '@/utils/interfaces/User';

interface BookCardProps {
  bundle: IBookShop;
  bookCount?: number;
  courseCount?: number;
}

const BundleShopCard = ({ bundle }: BookCardProps) => {
  const [loading, setLoading] = useState({ value: false, id: 0 });
  const localUserId = useRecoilValue(localUserID);
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const handleAddDataToCart = async (payload: AddToCartProps) => {
    try {
      setLoading({ value: true, id: payload.id });

      await addToCart(payload);
      notify(`${bundle.title} added to cart successfully!`, 'success');
      router.push('/cart');
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading({ value: false, id: payload.id });
    }
  };
  const countBooksAndCourses = (
    items?: Item[],
  ): { Books: number; Courses: number } => {
    if (!Array.isArray(items)) {
      return { Books: 0, Courses: 0 };
    }

    return items.reduce(
      (acc, item) => {
        if (item.category === 'Book' || item.category === 'E-Book') {
          acc.Books++;
        } else if (
          item.category === 'Recorded Course' ||
          item.category === 'Course'
        ) {
          acc.Courses++;
        }
        return acc;
      },
      { Books: 0, Courses: 0 },
    );
  };

  const { Books, Courses } = countBooksAndCourses(bundle?.products);

  return (
    <div
      onClick={() => {
        router.push(bundle.externalLink || '/');
      }}
      className='w-full h-full border-cartBgColor border  hover:bg-[#151515] card rounded-[12px] cursor-pointer flex flex-col transition duration-300'
    >
      <div className='h-full  card rounded-md text-fadedWhite'>
        <div className='flex flex-col justify-between items-center h-full w-full '>
          <div className='w-full max-h-[170px] py-3 md:pt-6 md:pb-0 flex justify-center h-full xl:p-5'>
            <Image
              src={bundle?.coverImage}
              alt={bundle?.title}
              width={326}
              height={256}
              className='object-contain rounded-md w-full'
            />
          </div>
          <div className='flex items-end flex-col p-4 lg:p-6 w-full'>
            <div className='w-full flex flex-col justify-center gap-y-1 py-4 md:py-0 items-center'>
              {(Books > 0 || Courses > 0) && (
                <span className='text-white text-sm border border-[#3C3B3E] py-1 px-3 rounded-full'>
                  {Books > 0 && `${Books} Book${Books > 1 ? 's' : ''}`}
                  {Books > 0 && Courses > 0 && ' + '}
                  {Courses > 0 && `${Courses} Course${Courses > 1 ? 's' : ''}`}
                </span>
              )}
              <span className='text-sm md:text-lg font-semibold'>
                {bundle?.title}
              </span>
              {bundle.is_available ? (
                <div className='flex items-center justify-center gap-2'>
                  <span className='text-lg font-semibold'>
                    {bundle?.currency}
                    {bundle?.price}
                  </span>
                  <span className='text-[#9F9EA6] line-through font-medium text-sm'>
                    {bundle?.currency}
                    {bundle?.originalPrice}
                  </span>
                  <span className='text-[#4ADE80] font-medium text-sm'>
                    {bundle?.currency}
                    {bundle?.discount}%
                  </span>
                </div>
              ) : (
                <div className='flex justify-center'>
                  <button className='px-2 py-0.5 md:py-1 mt-2 text-xs font-medium bg-grayZl-700  transition-colors border rounded-[4px] border-grayZl-600 text-white cursor-not-allowed'>
                    Currently Unavailable
                  </button>
                </div>
              )}
            </div>

            <div className='flex gap-4  w-full md:mt-4'>
              <ButtonDefault
                size={1}
                isWidthFull={true}
                rounded={false}
                roundedSm={true}
                variant={ButtonVariants.GRAY}
                enableScaling={false}
                onClick={() => {
                  router.push(bundle.externalLink || '/');
                }}
                className='w-full text-center text-sm group whitespace-nowrap'
              >
                <span className='text-sm font-semibold  px-3 xl:px-5'>
                  View Details
                </span>
              </ButtonDefault>
              <ShouldRender check={bundle?.is_available}>
                <ButtonDefault
                  size={1}
                  isWidthFull={true}
                  rounded={false}
                  roundedSm={true}
                  variant={ButtonVariants.PRIMARY}
                  enableScaling={false}
                  loading={bundle?.hardCopyId === loading.id && loading.value}
                  disabled={bundle?.hardCopyId === loading.id && loading.value}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleAddDataToCart({
                      id: bundle?.hardCopyId as number,
                      temp_user_id: localUserId.toString(),
                      quantity: 1,
                      trackingData: {
                        productData: {
                          price: bundle.price as number,
                          id: Number(bundle?.hardCopyId),
                          heading: bundle.title as string,
                          quantity: 1 as number,
                          currency: bundle?.i18n_attributes?.currency as string,
                        },
                        userData,
                        pathname: router.pathname,
                        productType: 'hardcopy',
                        type: 'bundles',
                      },
                    });
                  }}
                  className='w-full text-center text-sm group whitespace-nowrap'
                >
                  Add to Cart
                </ButtonDefault>
              </ShouldRender>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleShopCard;
